<template>
	<div id="wow-sheet" :class="[sheet.closed?'locked':'', 'tab-'+tabState, subState, anonymousMode?'anonymous':'']">
		<!-- Toolbar for deleting rows -->
		<div class="toolbar fixed-top d-flex align-items-center px-3" v-if="selection.length > 0">
			<icon-button name="close" @click="clearSelection()" class="mr-2"></icon-button>
			<span class="title">{{$formatMessage('selection-count', {count: selection.length})}}</span>
			<div class="ml-auto">
				<icon-button @click="deleteSelection" name="delete" :disabled="deleting"></icon-button>
			</div>
		</div>

		<!-- Floating action button (FAB) to edit a row. Only visible on mobile -->
		<button class="btn-fab" @click="showEdit" v-if="sheet.isOpen"><i class="icon-add"></i></button>

		<div class="clearfix">

			<!-- Left column -->
			<div class="col-left">
				<div v-if="!loading">
					<!-- Empty state -->
					<div v-if="items.length === 0" class="card card-body" v-i18n="'expenses_void'"></div>
					<!-- Balance button for mobile -->
					<button v-else class="btn btn-link balance" @click="showBalance"
					        v-i18n="'sheet_counters_button'"></button>
				</div>
				<!-- List of rows -->
				<ul class="list-group list-transactions" ref="rowsList" :class="[loading?'mb-0':'']">
					<li v-for="item of items" :key="item.row.id" class="list-group-item"
					    :class="[{'bg-selected':item.selected}, {'bg-edited':item.edited}]"
					    :data-id="item.row.id">
						<!-- Avatar -->
						<div class="circle" :class="[{selected: item.selected}]">
							<input type="checkbox" v-if="item.selected || item.checkboxVisible"
							       :checked="item.selected"/>
							<avatar v-else :booker="item.row.booker"></avatar>
						</div>
						<!-- Amount -->
						<div class="amount bg-edited-child bg-selected-child">
							{{item.row.amount | format-currency(currency)}}
						</div>
						<!-- Booker name and split -->
						<div class="row w-100">
							<div class="text-truncate col-10"
							     :class="item.splitType === 'default' ? '' : 'col-sm-5'"
							>{{item.row.booker.name}}
							</div>
							<div class="col-4 d-none d-xl-block pl-0">
								<expense-split-badge :item="item" class="my-auto"></expense-split-badge>
							</div>
						</div>
						<!-- Comment, date and split -->
						<div class="text-secondary w-100">
							<user-text class="float-left" v-if="item.row.description"
							           :text="item.row.description"></user-text>
							<div class="float-right">
								<expense-split-badge :item="item" class="d-xl-none"></expense-split-badge>
								<div class="text-small"
								     :title="$formatMessage('expense_ctime', {ctime: item.row.ctime})"
								>{{item.row.ctime | format-date}}
								</div>
							</div>
						</div>
					</li>
				</ul>
				<!-- Loading rows spacer -->
				<loading-spacer v-if="!(allEntitiesLoaded)" class="mt-0"></loading-spacer>
			</div>

			<!-- Right column -->
			<div class="col-right">
				<div>
					<!-- Expense editor -->
					<expense-editor :sheet="sheet" :row-id="editedRowId" @cancelled="onEditCancelled"
					                @saved="onEditSaved" ref="expenseEditor"
					                :disabled="sheet.closed"
					></expense-editor>
					<!-- Balance -->
					<div class="sheet-balance-wrapper top-header">
						<sheet-balance :sheetKey="sheet.key" :balance="sheet.balance"
						               :currency="sheet.parent.currency"></sheet-balance>
					</div>
					<ul class="tabs tabs-up">
						<li v-i18n="'sheet_edit_tab'" @click="showTab('edit')"
						    :class="{active:tabState==='edit'}"></li>
						<li v-i18n="'sheet_counters_tab'" @click="showTab('counters')"
						    :class="{active:tabState==='counters'}"></li>
					</ul>
				</div>
			</div>
		</div>
		<router-view></router-view>
	</div>
</template>

<script>
	import store, {ChildrenObserver} from "../core/store";
	import ExpenseEditor from "./expense-editor.vue";
	import SheetBalance from "./sheet-balance.vue";
	import Avatar from "./widgets/avatar.vue";
	import IconButton from "./widgets/icon-button.vue";
	import UserText from "./widgets/user-text.vue";
	import toast from "../ui/toast";
	import ExpenseSplitBadge from "../ui/expense-split-badge.vue";
	import PimplWrapper from "../utils/pimpl";
	import ListFeed from "./widgets/listfeed";
	import LoadingSpacer from "./widgets/loading-spacer.vue";

	class Item {
		constructor(row) {
			this.row = row;
			this.selected = false;
			this.edited = false;
			this.checkboxVisible = false;
		}

		select() {
			this.selected = true;
		}

		getSplitProperty(propname, defaultValue) {
			return this.row.split ? this.row.split[propname] : defaultValue;
		}

		get splitType() {
			return this.getSplitProperty('type', 'default');
		}

		get splitTotalUnits() {
			return this.getSplitProperty('totalUnits', -1);
		}

		get splitBookerName() {
			const bookerId = this.getSplitProperty('bookerId', null);
			return bookerId ? this.row.getBooker(bookerId).name : "";
		}
	}

	class Pimpl {
		constructor(component) {
			this.component = component;
			this.items = new Map();
			this.onMouseHoverCircle = this._onMouseHoverCircle.bind(this);
			this.onRowClicked = this._onRowClicked.bind(this);
			this.onSelectionInputChanged = this._onSelectionInputChanged.bind(this);
			this.listFeed = new ListFeed(component, this, 'Row');
			this.afterLoadingRows = null;
		}

		init() {
			const component = this.component;
			this.listFeed.init(component.sheet.getLoader('Row', 20), window, component.$refs.rowsList).then(() => {
				this.component.loading = false;
				if (this.afterLoadingRows) {
					this.afterLoadingRows();
					delete this.afterLoadingRows;
				}
			});

			// Listen mouse hover events on the circle to show the checkbox
			$(component.$el)
				.on('mouseenter mouseleave touchstart', '.circle', this.onMouseHoverCircle)
				.on('change', '.list-group-item input', this.onSelectionInputChanged)
				.on('click', '.list-group-item', this.onRowClicked);
		}

		destroy() {
			$(this.component)
				.off('mouseenter mouseleave touchstart', '.circle', this.onMouseHoverCircle)
				.off('change', '.list-group-item input', this.onSelectionInputChanged)
				.off('click', '.list-group-item', this.onRowClicked);

			this.listFeed.destroy();
		}

		_onMouseHoverCircle(e) {
			if (this.component.sheet.isOpen) {
				const rowId = Number($(e.target).closest('[data-id]').attr('data-id'));
				this.items.get(rowId).checkboxVisible = e.type !== 'mouseleave';
			}
		}

		/** Called by ListFeeder */
		add(row, prepend) {
			if (!this.items.has(row.id)) {
				let item = new Item(row);
				this.component.items[prepend ? 'unshift' : 'push'](item);
				this.items.set(row.id, item);
				return true;
			}
		}

		/** Called by ListFeeder */
		remove(row) {
			const rowId = row.id;
			if (rowId === this.component.editedRowId) {
				this.component.editedRowId = null;
			}
			let index = this.component.items.findIndex((item) => item.row.id === rowId);
			if (index !== -1) {
				this.component.items.splice(index, 1);
				this.items.delete(rowId);
				return true;
			}
		}

		/** Called by ListFeeder */
		createObserver(observerImpl) {
			return new ChildrenObserver(this.component.sheet.key, observerImpl);
		}

		_onRowClicked(e) {
			const component = this.component;
			if (e.target.nodeName !== 'INPUT') {
				let rowId = Number($(e.target).closest('[data-id]').attr('data-id'));
				// toggle the checkbox when selection not empty
				if (component.selection.length > 0) {
					this._toggleRowSelection(rowId);
				} else {
					// otherwise edit the clicked row
					component.showEdit(rowId);
				}
			}
		}

		_toggleRowSelection(rowId, selected = undefined) {
			const item = this.items.get(Number(rowId));
			if (selected === undefined) {
				// toggle
				selected = !item.selected;
			}
			const selection = this.component.selection;
			if (selected) {
				selected = true;
				if (selection.indexOf(rowId) === -1) {
					selection.push(rowId);
				}
				this.unedit();
			} else {
				selected = false;
				selection.splice(selection.indexOf(rowId), 1);
			}
			item.selected = selected;
		}

		_onSelectionInputChanged(e) {
			let $input = $(e.target),
				rowId = Number($input.closest('[data-id]').attr('data-id'));
			this._toggleRowSelection(rowId, $input.is(':checked'));
		}

		unedit() {
			if (this.component.editedRowId) {
				this.items.get(this.component.editedRowId).edited = false;
				this.component.editedRowId = null;
			}
		}
	}

	export default {
		props: {
			sheet: Object,
			anonymousMode: Boolean
		},

		components: {
			Avatar,
			IconButton,
			ExpenseEditor,
			UserText,
			SheetBalance,
			ExpenseSplitBadge,
			LoadingSpacer
		},

		data() {
			return {
				loading: true,
				tabState: this.sheet.closed ? "counters" : "edit",
				subState: '',
				currency: this.sheet.parent.currency,
				items: [],
				deleting: false,
				editedRowId: null,
				selection: [],
				p: new PimplWrapper(new Pimpl(this)),
				allEntitiesLoaded: false
			};
		},

		created() {
			this.$on('enterState', this.enterState.bind(this));
			this.$on('exitState', this.exitState.bind(this));
		},

		mounted() {
			this.p.impl.init();
			$('body').toggleClass('wow-sheet-body', true);
		},

		destroyed() {
			this.p.impl.destroy();
			$('body').toggleClass('wow-sheet-body', false);
		},

		methods: {
			clearSelection() {
				for (let item of this.items) {
					item.selected = false;
				}
				this.selection.splice(0);
			},
			deleteSelection() {
				this.deleting = true;
				const rows = this.selection.map((rowId) => {
					return store.get(this.sheet.key, 'Row', rowId);
				});
				this.sheet.removeChildren('Row', this.selection)
					.then(() => {
						this.selection.splice(0);
						toast.cancel(
							this.$formatMessage("expense_deleted", {count: rows.length}),
							() => this.sheet.addChildren(rows));
					})
					.always(() => {
						this.deleting = false;
					});
			},
			gotoState(stateName, stateParams = {}) {
				const route = {name: stateName, params: stateParams};
				if (this.$route.name === stateName) {
					// when in desktop mode, avoid polluting the history
					this.$router.replace(route);
				} else {
					this.$router.push(route);
				}
			},
			enterState(stateName, stateParams) {
				if (this.loading) {
					this.p.impl.afterLoadingRows = () => {
						this.enterState(stateName, stateParams);
					};
				} else {
					this.tabState = stateName === 'balance' ? 'counters' : 'edit';
					this.clearSelection();
					if (this.editedRowId) {
						this.p.impl.items.get(this.editedRowId).edited = false;
					}
					if (stateParams.rowId) {
						const rowId = parseInt(stateParams.rowId);
						const rowItem = this.p.impl.items.get(rowId);
						if (rowItem) {
							rowItem.edited = true;
						}
						this.editedRowId = rowId;
					}
					this.subState = `substate substate-${stateName}`;
				}
			},
			exitState() {
				this.tabState = 'edit';
				this.subState = '';
				this.p.impl.unedit();
			},
			showEdit(rowId) {
				this.$refs.expenseEditor.disableCancelWhenPristine = false;
				if (typeof rowId === 'number') {
					this.gotoState("editRow", {rowId});
				} else {
					this.gotoState("newRow");
				}
			},
			showBalance() {
				this.gotoState("balance");
			},
			onEditCancelled() {
				let backUrl = this.$route.meta.backUrl;
				if (backUrl) { // when updating a row or when adding a new one on mobile
					if (typeof(backUrl) === 'function')
						backUrl = backUrl(this.$route);
					this.$router.replace(backUrl);
				} else {
					// when adding a new row on desktop
					this.p.impl.unedit();
				}
				this.$refs.expenseEditor.disableCancelWhenPristine = true;
			},
			onEditSaved() {
				this.$refs.expenseEditor.reset();
				if (this.editedRowId) {
					this.onEditCancelled();
				}
			},
			showTab(name) {
				this.gotoState(name === 'edit'? 'sheet' : 'balance');
			}
		},

		watch: {
			selection() {
				$(document.body).toggleClass("navbar-mini", this.selection.length > 0);
			}
		}
	}
</script>

<style lang="scss">
	@import "common";
	@import "sheet";
	@import "~bootstrap/scss/functions";
	@import "~bootstrap/scss/variables";
	@import "~bootstrap/scss/mixins/breakpoints";

	#wow-sheet {
		$col-right-width: 16.6rem;

		.col-right {
			display: none;
		}

		.btn.balance-back {
			display: none;
		}

		&.substate {
			.col-right {
				display: block;
			}
			.col-left, .btn-fab {
				display: none;
			}
			@include large_screen() {
				.col-left {
					display: block;
				}
			}
		}

		.tabs {
			display: none;
			@media print {
				display: none !important;
			}
		}

		.sheet-balance {
			display: none;
			tr th:first-child {
				max-width: 0;
				width: 40%;
			}
		}
		@include large_screen() {
			.btn-fab {
				display: none;
			}
			.sheet-balance {
				display: table;
			}
			ul.tabs {
				display: block;
				position: fixed; // not absolute to make it work on UC Browser
				width: $col-right-width;
				bottom: 0;
				margin: 0.5rem 0;
				border-top: solid 1px rgba(0, 0, 0, 0.12);
				li {
					width: 49%;
					text-align: center;
					cursor: pointer;
				}
			}
		}

		&.substate-balance {
			.expense-editor {
				display: none;
			}
			.sheet-balance {
				display: table;
			}
			.btn.balance-back {
				display: block;
			}
		}

		@include large_screen() {
			.col-left {
				display: block;
				float: left;
				width: 30rem;
				max-width: calc(100% - 18rem);
			}
			.col-right {
				display: block;
				float: right;
				&, > div {
					width: $col-right-width;
				}
				> div {
					position: fixed;
					overflow: auto;
					height: calc(100% - 7.5rem);
				}
			}
			.btn.balance {
				display: none;
			}
			&.tab-edit {
				.sheet-balance {
					display: none;
				}
			}
			&.tab-counters {
				.expense-editor {
					display: none;
				}
			}
		}

		@include xlarge_screen(#wow-sheet) {
			@include sheet-container;
			.col-right {
				.sheet-balance {
					display: table !important;
				}
				.expense-editor {
					display: block !important;
				}
				.sheet-balance-wrapper {
					position: fixed;
					bottom: 0;
					right: 0;
					width: $panel-width;
					background-color: #DCDCDC;
					// Fixed chrome blinking: http://stackoverflow.com/a/40306583/287058
					-webkit-transform: translateZ(0);
					.sheet-balance {
						position: absolute;
						bottom: 0;
					}
				}
				.tabs {
					display: none;
				}
			}
		}

		.list-group {
			display: block; // prevent bootstrap flexbox
		}

		@media print {
			.expense-editor {
				display: none;
			}
		}
	}

	body.wow-sheet-body {
		.expense-editor button.btn-secondary {
			display: none;
		}
		#header button.btn-back+button.btn-menu {
			display: none;
		}
		@include large_screen() {
			#header {
				button.btn-back {
					display: none;
				}
				button.btn-back+button.btn-menu {
					display: inline-block;
					margin-left: 0;
				}
			}
			.expense-editor button.btn-secondary {
				display: inline-block;
			}
		}
		@include media-breakpoint-up(lg) {
			#header button.btn-back+button.btn-menu {
				display: none;
			}
		}
	}
</style>



// WEBPACK FOOTER //
// src/ui/sheet-expenses.vue